import React, { useState } from 'react';
import { Datagrid, FunctionField, TextField } from 'react-admin';
import RaList from '../../components/ra-list/RaList';
import RenderControls from '../../components/ra-list/RenderControls';
import { dataProvider } from '../../DataProvider';
import { formatDateTime } from '../../components/ex_dayjs';
import { loadSourceType } from '../../utils/common';
import { DeliveryMethodEnum } from '../../utils/enum';
import { FilterListProvider, useFilterList } from '../../context/FilterListProvider';
import './style.css';

/** Simple regex-based e-mail format validator. */
const isValidEmail = (email: string): boolean =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

interface MarketChoice {
    id: string;
    name: string;
}

/**
 * Inner filter form rendered inside the react-admin List context.
 * Uses the standard RenderControls filter mechanism (no onSearch override) so
 * that the Filter button correctly triggers the react-admin data-fetch flow.
 * Inline validation messages guide the user without blocking the search.
 */
const EmailSearchFilter: React.FC<{ marketType: MarketChoice[] }> = ({ marketType }) => {
    const [emailValue, setEmailValue] = useState('');

    const handleEmailChange = ({ value }: { value: string }) => {
        setEmailValue(value ?? '');
    };

    const controls = [
        {
            component: 'text',
            name: 'email_eq',
            label: 'Email',
            xs: 3,
            onChange: handleEmailChange,
        },
        {
            component: 'select',
            name: 'event_market_eq',
            label: 'Market',
            choices: marketType,
            xs: 2,
        },
    ];

    const emailEmpty = !emailValue.trim();
    const emailInvalid = !emailEmpty && !isValidEmail(emailValue);

    return (
        <div className="email-search-filter">
            <RenderControls controls={controls} />
            {emailEmpty && (
                <div className="email-search-hint">
                    Enter a valid email address to search.
                </div>
            )}
            {emailInvalid && (
                <div className="email-search-error">
                    Please enter a valid email address.
                </div>
            )}
        </div>
    );
};

/**
 * Email Search Tool page – lets admins look up orders by e-mail address and
 * (optionally) event market.  An order matches when the e-mail equals the
 * order's Primary Account or appears inside a matching VividOrderNote post.
 *
 * Route: /EmailSearchTool/
 */
const EmailSearchTool: React.FC = () => {
    const [sortModel] = useState<string[]>(['order_date desc']);
    const [marketType, setMarketType] = useState<MarketChoice[]>([]);

    React.useEffect(() => {
        loadSourceType(dataProvider, (response: MarketChoice[]) => setMarketType(response));
    }, []);

    return (
        <FilterListProvider>
            <RaList
                title="Email Search Tool"
                perPage={50}
                pagination={[50, 100, 250, 500]}
                sortby={sortModel}
                filters={<EmailSearchFilter marketType={marketType} />}
            >
                <EmailSearchResultsGrid />
            </RaList>
        </FilterListProvider>
    );
};

const EmailSearchResultsGrid: React.FC = () => {
    const [filterList] = useFilterList() || [{}];

    const deliveryMethodName = (id: number | null | undefined): string => {
        if (!id) return '';
        return DeliveryMethodEnum.find((m: any) => parseInt(m.id) === id)?.name ?? String(id);
    };

    const searchedEmail = String(filterList?.email_eq ?? '').trim();
    const escapedSearchEmail = searchedEmail.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    const highlightMatch = (value?: string) => {
        if (!value) {
            return '';
        }
        if (!searchedEmail) {
            return value;
        }
        const pattern = new RegExp(`(${escapedSearchEmail})`, 'ig');
        const parts = value.split(pattern);

        return (
            <>
                {parts.map((part, index) => (
                    index % 2 === 1
                        ? (
                            <span key={`${index}-${part}`} className="email-search-match-highlight">
                                {part}
                            </span>
                        )
                        : <React.Fragment key={`${index}-${part}`}>{part}</React.Fragment>
                ))}
            </>
        );
    };

    return (
        <Datagrid optimized bulkActionButtons={false}>
            <TextField source="order_number" label="Order #" />
            <FunctionField
                source="order_date"
                label="Order Date"
                render={(record: any) => formatDateTime(record.order_date)}
            />
            <TextField source="event" label="Event" />
            <TextField source="venue" label="Venue" />
            <FunctionField
                source="event_date"
                label="Event Date"
                render={(record: any) => formatDateTime(record.event_date)}
            />
            <TextField source="section" label="Section" />
            <TextField source="row" label="Row" />
            <TextField source="quantity" label="Qty" />
            <TextField source="event_market" label="Market" />
            <FunctionField
                source="delivery_method"
                label="Delivery"
                render={(record: any) => deliveryMethodName(record.delivery_method)}
            />
            <FunctionField
                source="primary_account"
                label="Primary Account"
                render={(record: any) => highlightMatch(record.primary_account)}
            />
            <FunctionField
                source="note_match"
                label="Note Match"
                render={(record: any) => highlightMatch(record.note_match)}
            />
        </Datagrid>
    );
};

const EmailSearchToolPage = {
    name: 'EmailSearchTool',
    list: EmailSearchTool,
};

export default EmailSearchToolPage;
